import * as React from "react";
import { DUPLICATE_STRATEGY } from "@/constants";
import { Box, DialogTitle, Grid, IconButton } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { ArrowLeftIcon } from "@phosphor-icons/react/dist/ssr/ArrowLeft";
import { ArrowRightIcon } from "@phosphor-icons/react/dist/ssr/ArrowRight";
import { FormProvider, useForm } from "react-hook-form";

import { FileMapping, useProcessImport } from "@/hooks/api/use-contacts";
import { ChipsFreeSolo } from "@/components/chips-free-solo";
import { CustomFormHelperError } from "@/components/customFormHelper/custom-form-helper-error";
import { FilterOption } from "@/components/customTable/types";
import { DynamicField } from "@/components/dynamicField/dynamic-field";
import { DynamicFieldType } from "@/components/dynamicField/types";
import { FilterFetchSelect } from "@/components/fetch-select";

export interface FileMappingModalProps {
	open: boolean;
	onClose: () => void;
	onSuccess: () => void;
	fileMapping: FileMapping;
	fields: any[];
}

const named = (name?: string) => Boolean(name) && !/^Column_\d+$/.test(name!);

export function FileMappingModal({
	open,
	onClose,
	onSuccess,
	fileMapping,
	fields,
}: FileMappingModalProps): React.JSX.Element {
	const [csvValues, setCsvValues] = React.useState<any[]>([]);
	const [activePreview, setActivePreview] = React.useState<number>(0);

  const totalPreview = fileMapping?.preview?.length || 0;
	// An upload's columns are auto-named (Column_0, ...); a source that knows its names, such as a
	// ZoomInfo audience, shows them so similar values (direct vs mobile phone) can be told apart.
	const optionLabel = (value: string, index: number) => {
		const name = fileMapping?.columns?.[index]?.name;
		if (!named(name)) return value;
		return value ? `${name}: ${value}` : name!;
	};
	// Such a source sends data rows only: offering "first line contains field names" would drop a contact.
	const namedColumns = fileMapping?.columns?.some((column) => named(column.name)) ?? false;

	const processImportMutation = useProcessImport();

	const methods = useForm<any>({
		defaultValues: {
			phone: "",
			duplicateStrategy: "",
			hasHeaders: false,
			tags: [],
			groupId: null,
			groupTitle: "",
			newGroupName: "",
		},
	});

	const {
		handleSubmit,
		reset,
		setError,
		control,
		getValues,
		setValue,
		watch,
		formState: { isSubmitting, errors },
		clearErrors,
	} = methods;

	const tags = watch("tags");
	const groupId = watch("groupId");
	const groupTitle = watch("groupTitle");

	React.useEffect(() => {
		const newFields = Object.fromEntries(fields.map((field) => [field.name, ""]));

		reset({
			phone: "",
			duplicateStrategy: "",
			hasHeaders: false,
			tags: [],
			groupId: null,
			groupTitle: "",
			newGroupName: "",
			...newFields,
		});

		if (!open) {
			setCsvValues([]);
			setActivePreview(0);
		}
	}, [fields, open, reset]);

	React.useEffect(() => {
		setCsvValues(fileMapping?.preview || []);
	}, [fileMapping]);

	const sendMapping = async () => {
		const values = getValues();
		const attributeMapping = fields.reduce((acc, field) => {
			const valueIndex = values[field.name];

			if (valueIndex === "" || valueIndex === undefined || valueIndex === null) {
				return acc;
			}

			return {
				...acc,
				[field.name]: Number(valueIndex),
			};
		}, {});

		try {
			await processImportMutation.mutateAsync({
				sessionId: fileMapping.sessionId,
				phoneColumn: values.phone === "" || values.phone === undefined || values.phone === null ? -1 : Number(values.phone),
				attributeMapping: attributeMapping,
				groupId: values.groupId,
				newGroupName: values.newGroupName,
				hasHeaders: values.hasHeaders,
				duplicateStrategy: values.duplicateStrategy,
				tags: values.tags.map((tag: any) => tag.label),
			});
			onClose();
			await onSuccess();
		} catch (error: any) {
			const errorMessages = error?.response?.data?.message;

			if (!Array.isArray(errorMessages)) {
				setError("root", { message: errorMessages });
				return;
			}

			const errorMessage = errorMessages[0];
			setError(errorMessage.split(" ")[0], { message: errorMessage });
		}
	};

	const handlePrevPreview = () => {
		if (activePreview > 0) {
			setActivePreview(activePreview - 1);
		}
	};

	const handleNextPreview = () => {
		if (activePreview < totalPreview - 1) {
			setActivePreview(activePreview + 1);
		}
	};

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
			<DialogTitle sx={{ p: 3, pb: 2 }}>
				<Stack direction="row" spacing={2} sx={{ display: "flex" }}>
					<Stack spacing={3}>
						<Stack spacing={1}>
							<Typography variant="h5">File Mapping</Typography>
							<Typography color="text.secondary" variant="body2">
								Please map the columns of the file to the fields of the contact.
							</Typography>
						</Stack>
					</Stack>
				</Stack>
			</DialogTitle>
			<DialogContent sx={{ px: 3 }}>
				<FormProvider {...methods}>
					<Grid
						container
						spacing={1}
						sx={{ p: 3, pt: 2, pb: 1, borderRadius: 2, border: "1px solid var(--mui-palette-divider)" }}
					>
						<Grid size={{ xs: 6, md: 6 }}>
							<Typography variant="body1" fontWeight={600}>
								Fields
							</Typography>
						</Grid>
						<Grid size={{ xs: 6, md: 6 }} display={{ xs: "none", md: "block" }}>
							<Typography variant="body1" fontWeight={600}>
								CSV Fields
							</Typography>
						</Grid>

						<Grid
							size={{ xs: 12, md: 6 }}
							sx={{
								display: "flex",
								alignItems: "center",
								border: "1px solid var(--mui-palette-divider)",
								pl: 1.5,
								borderRadius: "12px",
								backgroundColor: "var(--mui-palette-background-level1)",
								height: "40px",
							}}
						>
							<Typography variant="body1" fontSize="16px">
								Phone
							</Typography>
						</Grid>

						<Grid size={{ xs: 12, md: 6 }} sx={{ mt: -1 }}>
							<DynamicField
								control={control}
								type={DynamicFieldType.SELECT}
								name="phone"
								hideLabel
								label="Phone"
								dynamicField={{
									name: "phone",
									required: true,
									label: "Phone",
									type: DynamicFieldType.SELECT,
									options: csvValues?.[activePreview]?.map((value: string, index: number) => ({
										label: optionLabel(value, index),
										value: String(index),
									})),
                  isIndex: true,
								}}
							/>
						</Grid>

						{fields.map((field) => (
							<React.Fragment key={field.id}>
								<Grid
									size={{ xs: 12, md: 6 }}
									sx={{
										display: "flex",
										alignItems: "center",
										border: "1px solid var(--mui-palette-divider)",
										pl: 1.5,
										borderRadius: "12px",
										boxShadow: "var(--mui-shadows-1)",
										backgroundColor: "var(--mui-palette-background-level1)",
										height: "40px",
									}}
								>
									<Typography variant="body1" fontSize="16px">
										{field.label}
									</Typography>
								</Grid>

								<Grid size={{ xs: 12, md: 6 }} sx={{ mt: -1 }}>
									<DynamicField
										control={control}
										type={DynamicFieldType.SELECT}
										name={field.name}
										hideLabel
										label={field.label}
										dynamicField={{
											name: field.name,
											label: field.label,
											type: DynamicFieldType.SELECT,
											options: csvValues?.[activePreview]?.map((value: string, index: number) => ({
												label: optionLabel(value, index),
												value: String(index),
											})),
											isIndex: true,
										}}
									/>
								</Grid>
							</React.Fragment>
						))}
						<Grid
							size={{ xs: 12, md: 12 }}
							sx={{
								textAlign: "right",
								display: "flex",
								gap: 1,
								alignItems: "center",
								justifyContent: "flex-end",
								mt: 0.5,
							}}
						>
							{totalPreview > 1 && (
								<>
									<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
										<IconButton
											onClick={handlePrevPreview}
											disabled={activePreview === 0}
											sx={{ backgroundColor: "var(--mui-palette-background-level1)", width: 32, height: 32 }}
										>
											<ArrowLeftIcon width={14} height={14} />
										</IconButton>
										<IconButton
											onClick={handleNextPreview}
											disabled={activePreview === totalPreview - 1}
											sx={{ backgroundColor: "var(--mui-palette-background-level1)", width: 32, height: 32 }}
										>
											<ArrowRightIcon width={14} height={14} />
										</IconButton>
									</Box>
									<Typography variant="caption" fontSize="14px" color="text.secondary">
										{`${activePreview + 1} of ${totalPreview} records`}
									</Typography>
								</>
							)}
						</Grid>
					</Grid>

					<Grid
						container
						spacing={1}
						mt={2}
						sx={{
							p: 3,
							pt: 2,
							borderRadius: 2,
							border: "1px solid var(--mui-palette-divider)",
							alignItems: "flex-start",
						}}
					>
						<Grid size={{ xs: 12, md: 12 }}>
							<Typography variant="body1" fontWeight={600}>
								Settings
							</Typography>
						</Grid>
						<Grid
							size={{ xs: 12, md: 6 }}
							sx={{
								display: "flex",
								alignItems: "center",
								border: "1px solid var(--mui-palette-divider)",
								pl: 1.5,
								borderRadius: "12px",
								boxShadow: "var(--mui-shadows-1)",
								backgroundColor: "var(--mui-palette-background-level1)",
								minHeight: "40px",
							}}
						>
							<Typography variant="body1" fontSize="16px">
								Duplicate Strategy
							</Typography>
						</Grid>

						<Grid size={{ xs: 12, md: 6 }} sx={{ mt: -1 }}>
							<DynamicField
								control={control}
								type={DynamicFieldType.SELECT}
								name="duplicateStrategy"
								hideLabel
								label="Duplicate Strategy"
								dynamicField={{
									name: "duplicateStrategy",
									required: true,
									label: "Duplicate Strategy",
									type: DynamicFieldType.SELECT,
									options: DUPLICATE_STRATEGY,
								}}
							/>
						</Grid>

						{!namedColumns && (
							<>
								<Grid
									size={{ xs: 12, md: 6 }}
									sx={{
										display: "flex",
										alignItems: "center",
										border: "1px solid var(--mui-palette-divider)",
										pl: 1.5,
										borderRadius: "12px",
										boxShadow: "var(--mui-shadows-1)",
										backgroundColor: "var(--mui-palette-background-level1)",
										minHeight: "40px",
									}}
								>
									<Typography variant="body1" fontSize="16px">
										First line contains field names
									</Typography>
								</Grid>

								<Grid size={{ xs: 12, md: 6 }} sx={{ pl: 1 }}>
									{" "}
									<DynamicField
										control={control}
										type={DynamicFieldType.BOOLEAN}
										name="hasHeaders"
										hideLabel
										label="Has Headers"
										dynamicField={{
											name: "hasHeaders",
											label: "Has Headers",
											type: DynamicFieldType.BOOLEAN,
										}}
									/>
								</Grid>
							</>
						)}

						<Grid size={{ xs: 12, md: 6 }}>
							<FilterFetchSelect
								label="Group"
								showInputLabel
								placeholder="Select a group"
								onChange={(selected: FilterOption | string) => {
									const value = typeof selected === "string" ? selected : (selected.value as string) || "";
									const label = typeof selected === "string" ? selected : (selected.label as string) || "";

									setValue("groupId", value);
									setValue("groupTitle", label);
									clearErrors("groupId");
								}}
								getOptionsUrl="/crm/groups?isStatic=true"
								fetchLabel="name"
								fetchValue="id"
								errorText={errors.groupId?.message as string}
								value={{ label: groupTitle, value: groupId } as FilterOption}
							/>
							{errors.groupId ? <CustomFormHelperError text={(errors.groupId.message as string) || ""} /> : null}
						</Grid>
						{!groupId && (
							<Grid size={{ xs: 12, md: 6 }}>
								<DynamicField
									control={control}
									name="newGroupName"
									type={DynamicFieldType.TEXT}
									label="New Group Name"
									dynamicField={{
										name: "newGroupName",
										label: "New Group Name",
										type: DynamicFieldType.TEXT,
									}}
								/>
							</Grid>
						)}
						<Grid size={{ xs: 12, md: 12 }}>
							<ChipsFreeSolo
								placeholder="Enter tags"
								value={tags}
								label="Tags"
								setValue={(value) => setValue("tags", value)}
								withOptions
								getOptionsUrl="/crm/tags"
								fetchLabel="label"
								fetchValue="name"
								isTags
							/>
						</Grid>
					</Grid>
				</FormProvider>
			</DialogContent>
			<DialogActions sx={{ p: 3, py: 2 }}>
				<Stack direction="row" spacing={2} sx={{ justifyContent: "flex-end" }}>
					<Button color="secondary" onClick={onClose}>
						Cancel
					</Button>
					<Button
						variant="contained"
						onClick={handleSubmit(sendMapping)}
						disabled={isSubmitting || processImportMutation.isPending}
					>
						{processImportMutation.isPending ? "Processing..." : "Confirm"}
					</Button>
				</Stack>
			</DialogActions>
		</Dialog>
	);
}
